/**
 * ResultsSection - Match results for Americano (simple point-based scoring)
 */
import React, { useState } from 'react';
import ReactDOM from 'react-dom';
import { useTournament } from '../../contexts/TournamentContext';
import { useToast } from '../../contexts/ToastContext';
import './ResultsSection.css';

// ─── Match Score Card ─────────────────────────────────────────────────────────

const MatchScoreCard = ({ match, matchIndex, config, getPlayerName, onValidate, onUnvalidate, onScoreChange }) => {
  const total = config.pointsPerMatch;
  const savedT1 = match.detailedScore?.team1Points;
  const savedT2 = match.detailedScore?.team2Points;

  const [t1, setT1] = useState(typeof savedT1 === 'number' ? savedT1 : null);
  const [t2, setT2] = useState(typeof savedT2 === 'number' ? savedT2 : null);
  const [activeTeam, setActiveTeam] = useState(null);

  const isValidated = match.validated === true;
  const isComplete = t1 !== null && t2 !== null && t1 + t2 === total;
  const courtName = config.courtNames?.[match.courtIndex] || `Terrain ${match.courtIndex + 1}`;
  const team1Names = match.team1.map(id => getPlayerName(id)).join(' & ');
  const team2Names = match.team2.map(id => getPlayerName(id)).join(' & ');

  React.useEffect(() => {
    const st1 = match.detailedScore?.team1Points;
    const st2 = match.detailedScore?.team2Points;
    if (typeof st1 === 'number') setT1(st1);
    if (typeof st2 === 'number') setT2(st2);
  }, [match.detailedScore]);

  const handleFieldClick = (team) => {
    if (isValidated) return;
    setActiveTeam(prev => prev === team ? null : team);
  };

  const handleSelect = (n) => {
    if (activeTeam === 1) {
      setT1(n);
      setT2(total - n);
      onScoreChange(matchIndex, n, total - n);
    } else {
      setT2(n);
      setT1(total - n);
      onScoreChange(matchIndex, total - n, n);
    }
    setActiveTeam(null);
  };

  const handleValidate = () => {
    if (!isComplete) return;
    onValidate(matchIndex, t1, t2);
  };

  return (
    <div className={`match-score-card ${isValidated ? 'match-validated' : ''}`}>

      {/* Header */}
      <div className="match-card-header">
        <span className="match-court-label">
          <i className="bi bi-geo-alt-fill me-1"></i>{courtName}
        </span>
        {isValidated ? (
          <button className="btn btn-sm btn-outline-light" onClick={() => onUnvalidate(matchIndex)}>
            <i className="bi bi-pencil-fill"></i>
          </button>
        ) : (
          <button className="btn btn-sm btn-success" onClick={handleValidate} disabled={!isComplete}>
            <i className="bi bi-check-circle me-1"></i>Valider
          </button>
        )}
      </div>

      {/* Score rows — cliquables */}
      <div className="match-score-rows">
        <div
          className={`match-score-row ${activeTeam === 1 ? 'match-score-row--active' : ''} ${isValidated && t1 > t2 ? 'match-score-row--winner' : ''}`}
          onClick={() => handleFieldClick(1)}
        >
          <span className="match-score-team">{team1Names}</span>
          <span className="match-score-value">{t1 !== null ? t1 : '—'}</span>
        </div>
        <div
          className={`match-score-row ${activeTeam === 2 ? 'match-score-row--active' : ''} ${isValidated && t2 > t1 ? 'match-score-row--winner' : ''}`}
          onClick={() => handleFieldClick(2)}
        >
          <span className="match-score-team">{team2Names}</span>
          <span className="match-score-value">{t2 !== null ? t2 : '—'}</span>
        </div>
      </div>

      {/* Matrice — modal centrée / bottom sheet mobile */}
      {activeTeam !== null && !isValidated && ReactDOM.createPortal(
        <div className="score-modal-overlay" onClick={() => setActiveTeam(null)}>
          <div className="score-modal" onClick={e => e.stopPropagation()}>
            <div className="score-modal-header">
              <p className="score-modal-label">
                Score de <strong>{activeTeam === 1 ? team1Names : team2Names}</strong>
              </p>
              <button className="score-modal-close" onClick={() => setActiveTeam(null)}>
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
            <div className="score-matrix">
              {Array.from({ length: total + 1 }, (_, i) => i).map(n => {
                const isSelected = activeTeam === 1 ? t1 === n : t2 === n;
                return (
                  <button
                    key={n}
                    type="button"
                    className={`score-matrix-btn ${isSelected ? 'score-matrix-btn--selected' : ''}`}
                    onClick={() => handleSelect(n)}
                  >
                    {n}
                  </button>
                );
              })}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

// ─── ResultsSection ───────────────────────────────────────────────────────────

const ResultsSection = () => {
  const {
    state,
    getPlayerName,
    validateMatch,
    unvalidateMatch,
    setCurrentRound,
    advanceRound,
    startTournament,
    finishTournament
  } = useTournament();
  const { showError } = useToast();

  const { schedule, currentRound, config, tournamentStarted } = state;

  if (!schedule?.rounds?.length) {
    return (
      <div className="section-inner">
        <div className="section-header">
          <h1><i className="bi bi-clipboard-check me-3"></i>Résultats</h1>
        </div>
        <div className="section-body">
          <div className="alert alert-warning">Aucun planning généré.</div>
        </div>
      </div>
    );
  }

  const round = schedule.rounds[currentRound];
  if (!round) return null;

  const totalRounds = schedule.rounds.length;
  const isLastRound = currentRound === totalRounds - 1;
  const validatedCount = round.matches.filter(m => m.validated).length;
  const totalMatches = round.matches.length;
  const allValidated = validatedCount === totalMatches;

  const handleScoreChange = () => {
    // Scores tracked locally in MatchScoreCard; validation writes to context
  };

  const handleValidate = (matchIndex, team1Points, team2Points) => {
    validateMatch(currentRound, matchIndex, team1Points, team2Points);
    if (!tournamentStarted) startTournament();
  };

  const handleUnvalidate = (matchIndex) => {
    unvalidateMatch(currentRound, matchIndex);
  };

  const handleNext = () => {
    if (!allValidated) {
      showError('Validez tous les terrains avant de continuer');
      return;
    }
    if (isLastRound) {
      finishTournament();
      const rankingEl = document.getElementById('section-ranking');
      if (rankingEl) rankingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      advanceRound();
      setCurrentRound(currentRound + 1);
    }
  };

  const handlePrev = () => {
    if (currentRound > 0) setCurrentRound(currentRound - 1);
  };

  return (
    <div className="section-inner">
      <div className="section-header">
        <h1><i className="bi bi-clipboard-check me-3"></i>Résultats</h1>
      </div>

      <div className="section-body">
        {/* Round indicator */}
        <div className="d-flex justify-content-center mb-3">
          <span className="badge bg-warning text-dark fs-6">
            Tour {currentRound + 1} / {totalRounds}
          </span>
        </div>

        {/* Progress */}
        <div className={`alert ${allValidated ? 'alert-success' : validatedCount > 0 ? 'alert-warning' : 'alert-secondary'} mb-4`}>
          <i className="bi bi-clipboard-check me-2"></i>
          <strong>Progression :</strong> {validatedCount}/{totalMatches} terrains validés
        </div>

        {/* Matches */}
        <div className="matches-results mb-4">
          {round.matches.map((match, matchIndex) => (
            <MatchScoreCard
              key={matchIndex}
              match={match}
              matchIndex={matchIndex}
              config={config}
              getPlayerName={getPlayerName}
              onScoreChange={handleScoreChange}
              onValidate={handleValidate}
              onUnvalidate={handleUnvalidate}
            />
          ))}
        </div>

        {/* Resting players */}
        {round.restingPlayers?.length > 0 && (
          <div className="p-3 bg-light rounded mb-4">
            <h6 className="text-muted mb-2">
              <i className="bi bi-pause-circle me-2"></i>Joueurs au repos
            </h6>
            <div className="d-flex flex-wrap gap-2">
              {round.restingPlayers.map(id => (
                <span key={id} className="badge bg-secondary">
                  <i className="bi bi-person me-1"></i>{getPlayerName(id)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="d-flex flex-wrap gap-2 justify-content-between">
          {currentRound > 0 && (
            <button className="btn btn-outline-light" onClick={handlePrev}>
              <i className="bi bi-chevron-left me-2"></i>
              <span className="btn-text-desktop">Tour précédent</span>
            </button>
          )}

          {!isLastRound ? (
            <button
              className="btn btn-outline-light ms-auto"
              onClick={handleNext}
              disabled={!allValidated}
            >
              <span className="btn-text-desktop">Tour suivant</span>
              <i className="bi bi-chevron-right ms-2"></i>
            </button>
          ) : (
            <button
              className="btn btn-success btn-lg ms-auto"
              onClick={handleNext}
              disabled={!allValidated}
            >
              <i className="bi bi-trophy-fill me-2"></i>Voir le classement
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResultsSection;
